// Parsing and checking lab results typed by a technologist (docs/plans/phase1-lab.md §5.2).
//
// Three result types: numeric (a number in the analyte's unit, possibly censored as
// "< 0.5" or "> 1000" at the limits of a method), coded (one of the analyte's answer
// options) and text. A numeric value becomes a scaled integer (value × 10^decimals), so
// nothing downstream uses floating point, as vital signs do.
//
// Nothing here judges a value against a range: that is interpretation, and every range
// comes from the clinical lead's configuration.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export const LAB_RESULT_TYPES = ['numeric', 'coded', 'text'] as const;
export type LabResultType = (typeof LAB_RESULT_TYPES)[number];

/** FHIR Quantity.comparator, in our words: less than, at most, more than, at least. */
export const LAB_COMPARATORS = ['lt', 'le', 'gt', 'ge'] as const;
export type LabComparator = (typeof LAB_COMPARATORS)[number];

const COMPARATOR_SYMBOLS: Record<LabComparator, string> = {
  lt: '<',
  le: '≤',
  gt: '>',
  ge: '≥',
};

/** The most decimal places an analyte may take. Keeps scaled values exact in a double. */
export const MAX_LAB_DECIMALS = 4;

/** Digits before the decimal point. Room for counts such as viral loads; stops overflow. */
const MAX_INTEGER_DIGITS = 9;

/** The longest text result, matching the API schema. */
export const MAX_LAB_TEXT_LENGTH = 2000;

/** The shortest reason for "Not done", matching the shared reason schema. */
const MIN_REASON_LENGTH = 5;

export type LabValueErrorCode =
  | 'lab_value.empty'
  | 'lab_value.not_a_number'
  | 'lab_value.negative'
  | 'lab_value.too_many_decimals'
  | 'lab_value.too_large'
  | 'lab_value.comparator_only'
  | 'lab_value.unknown_option'
  | 'lab_value.option_retired'
  | 'lab_value.too_long'
  | 'lab_value.wrong_kind'
  | 'lab_value.reason_required';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const LAB_VALUE_ERRORS: Record<LabValueErrorCode, string> = {
  'lab_value.empty': 'Enter a result, or mark the test not done with a reason',
  'lab_value.not_a_number': 'Enter numbers only, without the unit',
  'lab_value.negative': 'This result cannot be negative',
  'lab_value.too_many_decimals': 'Too many decimal places for this result',
  'lab_value.too_large': 'The number is too long',
  'lab_value.comparator_only': 'Enter a number after the < or > sign',
  'lab_value.unknown_option': 'Choose one of the listed results',
  'lab_value.option_retired': 'That result option is no longer in use',
  'lab_value.too_long': 'The result text is too long',
  'lab_value.wrong_kind': 'This test takes a different kind of result',
  'lab_value.reason_required': 'Say why the test was not done',
};

export interface LabNumericFormat {
  /** 0 to MAX_LAB_DECIMALS. */
  readonly decimals: number;
  /** A few analytes can be negative (base excess); most cannot. */
  readonly allowsNegative: boolean;
}

export interface LabNumericValue {
  /** The value × 10^decimals. */
  readonly scaled: number;
  readonly decimals: number;
  /** Set when the value is censored ("< 0.5"). */
  readonly comparator: LabComparator | null;
}

function assertFormat(format: LabNumericFormat): void {
  if (
    !Number.isInteger(format.decimals) ||
    format.decimals < 0 ||
    format.decimals > MAX_LAB_DECIMALS
  ) {
    throw new RangeError(`Lab decimals must be a whole number from 0 to ${MAX_LAB_DECIMALS}`);
  }
}

/** Longest symbols first, so "<=" isn't read as "<" followed by "=". */
const COMPARATOR_PREFIXES: readonly (readonly [string, LabComparator])[] = [
  ['<=', 'le'],
  ['>=', 'ge'],
  ['≤', 'le'],
  ['≥', 'ge'],
  ['<', 'lt'],
  ['>', 'gt'],
];

/**
 * A typed numeric result: an optional comparator, an optional minus sign where the analyte
 * allows it, digits, and at most the analyte's decimals after "." or ",". Thousands
 * separators, units, exponents and extra decimal places (even zeros: "1,000" could be a
 * thousand) are refused, never guessed.
 */
export function parseLabNumeric(
  text: string,
  format: LabNumericFormat,
): Result<LabNumericValue, LabValueErrorCode> {
  assertFormat(format);
  let rest = text.trim();
  if (rest === '') return err('lab_value.empty');

  let comparator: LabComparator | null = null;
  for (const [symbol, value] of COMPARATOR_PREFIXES) {
    if (rest.startsWith(symbol)) {
      comparator = value;
      rest = rest.slice(symbol.length).trim();
      break;
    }
  }
  if (comparator !== null && rest === '') return err('lab_value.comparator_only');

  let negative = false;
  if (rest.startsWith('-') || rest.startsWith('−')) {
    if (!format.allowsNegative) return err('lab_value.negative');
    negative = true;
    rest = rest.slice(1);
  }

  const match = /^(\d+)(?:[.,](\d*))?$/.exec(rest);
  if (match === null) return err('lab_value.not_a_number');
  const integerPart = (match[1] ?? '').replace(/^0+(?=\d)/, '');
  const fraction = match[2] ?? '';
  if (fraction.length > format.decimals) return err('lab_value.too_many_decimals');
  if (integerPart.length > MAX_INTEGER_DIGITS) return err('lab_value.too_large');

  const magnitude = Number(integerPart + fraction.padEnd(format.decimals, '0'));
  // "-0" is zero: a sign on nothing carries no meaning.
  const scaled = negative && magnitude !== 0 ? -magnitude : magnitude;
  return ok({ scaled, decimals: format.decimals, comparator });
}

/** The number alone as a decimal string, e.g. for a `numeric` column: 425 at 2 dp → "4.25". */
export function labNumericToDecimalString(value: LabNumericValue): string {
  if (!Number.isSafeInteger(value.scaled)) {
    throw new RangeError('A scaled lab value must be a whole number');
  }
  const sign = value.scaled < 0 ? '-' : '';
  const digits = String(Math.abs(value.scaled));
  if (value.decimals === 0) return `${sign}${digits}`;
  const padded = digits.padStart(value.decimals + 1, '0');
  return `${sign}${padded.slice(0, -value.decimals)}.${padded.slice(-value.decimals)}`;
}

/** The value as a person reads it, with its comparator: "< 0.50", "≥ 1000", "-2.0". */
export function formatLabNumeric(value: LabNumericValue): string {
  const number = labNumericToDecimalString(value).replace(/^-/, '−');
  return value.comparator === null ? number : `${COMPARATOR_SYMBOLS[value.comparator]} ${number}`;
}

export interface LabAnswerOption {
  readonly code: string;
  readonly display: string;
  readonly status: 'active' | 'inactive';
}

/** A coded result must be one of the analyte's options, and still in use. */
export function validateCodedResult(
  optionCode: string,
  options: readonly LabAnswerOption[],
): Result<LabAnswerOption, LabValueErrorCode> {
  if (optionCode.trim() === '') return err('lab_value.empty');
  const option = options.find((o) => o.code === optionCode);
  if (option === undefined) return err('lab_value.unknown_option');
  if (option.status !== 'active') return err('lab_value.option_retired');
  return ok(option);
}

/** A text result, trimmed; not blank and not longer than the stored limit. */
export function validateTextResult(text: string): Result<string, LabValueErrorCode> {
  const trimmed = text.trim();
  if (trimmed === '') return err('lab_value.empty');
  if (trimmed.length > MAX_LAB_TEXT_LENGTH) return err('lab_value.too_long');
  return ok(trimmed);
}

/** What the analyte takes, as far as checking a typed result is concerned. */
export type LabAnalyteShape =
  | { readonly resultType: 'numeric'; readonly format: LabNumericFormat }
  | { readonly resultType: 'coded'; readonly options: readonly LabAnswerOption[] }
  | { readonly resultType: 'text' };

/** What the technologist entered for one analyte. */
export type LabResultInput =
  | { readonly kind: 'numeric'; readonly text: string }
  | { readonly kind: 'coded'; readonly optionCode: string }
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'not_done'; readonly reason: string };

export type LabResultValue =
  | { readonly kind: 'numeric'; readonly value: LabNumericValue }
  | { readonly kind: 'coded'; readonly option: LabAnswerOption }
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'not_done'; readonly reason: string };

/**
 * Checks one entered result against what its analyte takes. A result of the wrong kind is
 * refused rather than converted. "Not done" is allowed for any analyte, with a reason.
 */
export function validateResultInput(
  analyte: LabAnalyteShape,
  input: LabResultInput,
): Result<LabResultValue, LabValueErrorCode> {
  if (input.kind === 'not_done') {
    const reason = input.reason.trim();
    if (reason.length < MIN_REASON_LENGTH) return err('lab_value.reason_required');
    return ok({ kind: 'not_done', reason });
  }
  if (input.kind !== analyte.resultType) return err('lab_value.wrong_kind');

  if (analyte.resultType === 'numeric' && input.kind === 'numeric') {
    const parsed = parseLabNumeric(input.text, analyte.format);
    return parsed.ok ? ok({ kind: 'numeric', value: parsed.value }) : parsed;
  }
  if (analyte.resultType === 'coded' && input.kind === 'coded') {
    const option = validateCodedResult(input.optionCode, analyte.options);
    return option.ok ? ok({ kind: 'coded', option: option.value }) : option;
  }
  if (input.kind === 'text') {
    const text = validateTextResult(input.text);
    return text.ok ? ok({ kind: 'text', text: text.value }) : text;
  }
  return err('lab_value.wrong_kind');
}
